import * as React from "react";

import { PrintSettingsElementEnum } from "@app/access-reports/admin";
import {
  AccessReportsPrintOptions,
  AccessReportsWizardElement,
  AccessReportsWizardElementTypeEnum,
  AccessReportsWizardFormGroup,
  AccessReportsWizardModel,
  AccessReportsWizardPage,
  AccessReportsWizardStepEnum,
  AccessReportsWizardTemplate,
  AccessReportsWizardVersionModel,
} from "@app/access-reports/shared";
import { QESButton, QESCheckbox } from "@app/shared/qes-standards";
import { flatten, isEqual } from "lodash";
import { ColorResult, SketchPicker } from "react-color";
import { Modal } from "@app/shared/Modals";
import { JSX } from "react";

interface AccessReportsAdminPrintSettingsTabProps {
  accessReportsWizard: AccessReportsWizardModel;
  disableUserActions: boolean;
  onPrintOptionsChange: (formGroupId: number, key: keyof AccessReportsPrintOptions, defaultValue: string | boolean) => void;
  onResetPrintSettingsClick: () => void;
  onSaveClick: () => void;
  selectedVersion: AccessReportsWizardVersionModel;
}

interface AccessReportsAdminPrintSettingsTabState {
  displayColorPicker: PrintSettingsElementEnum
  displayResetAlert: boolean;
}

class AccessReportsAdminPrintSettingsTab extends React.Component<AccessReportsAdminPrintSettingsTabProps, AccessReportsAdminPrintSettingsTabState> {
  private defaultsButtonRef = React.createRef<HTMLButtonElement>();

  constructor(props: AccessReportsAdminPrintSettingsTabProps) {
    super(props);

    this.state = {
      displayColorPicker: null,
      displayResetAlert: false,
    };
  }

  public render() {
    return this.builtPrintOptionsSection();
  }


  private builtPrintOptionsSection = () => {
    const printOptionsPage: AccessReportsWizardPage = this.props.selectedVersion.template.pages.find((x) => x.title === AccessReportsWizardStepEnum.PrintOptions);

    return (
      <div className="tab-pane fade" id="print-settings">
        {this.state.displayResetAlert && (
          <Modal
            closeModal={() => this.hideResetAlert(false)}
            content={{
              body: "Are you sure you want to restore the default values for all print settings?",
              cancelButtonText: "Cancel",
              confirmButtonAction: () => this.hideResetAlert(true),
              confirmButtonText: "Reset",
              title: "Warning",
            }}
            isVisible={this.state.displayResetAlert}
            hideBodyScroll={true}
            triggerElement={this.defaultsButtonRef}
          />
        )}

        <div className="row">
          <div className="col-12">
            <div className="d-flex justify-content-end mb-3">
              <QESButton
                disabled={this.disableSave(printOptionsPage)}
                qesType="primary"
                qesTestId={"access-reports-admin__dashboard--print-settings-save"}
                onClick={() => this.props.onSaveClick()}
              >
                Save
              </QESButton>
            </div>
          </div>
        </div>

        <div className="row">
          {printOptionsPage.formGroups.map((formGroup, formGroupIndex) => (
            <div
              className={`col-md-4${formGroupIndex !== 0 ? " pl-5" : ""}${(formGroupIndex !== printOptionsPage.formGroups.length - 1) ? " pr-5 access-print-options-divider" : ""}`}
              key={formGroupIndex}
            >
              {formGroup.elements.map((element, elementIndex) => (
                <div className="row mb-3" key={elementIndex}>
                  {this.buildFormElement(printOptionsPage, formGroup, element)}
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className="row">
          <div className="col-12">
            <div className="d-flex justify-content-end mb-3">
              <QESButton
                disabled={this.disableReset(printOptionsPage)}
                qesType="primary"
                qesTestId={"access-reports-admin__dashboard--print-settings-reset"}
                onClick={() => this.displayResetAlert()}
                qesRef={this.defaultsButtonRef}
              >
                Restore Defaults
              </QESButton>
            </div>

          </div>
        </div>
      </div>
    );
  }

  private buildFormElement = (
    printOptionsPage: AccessReportsWizardPage,
    formGroup: AccessReportsWizardFormGroup,
    element: AccessReportsWizardElement
  ): JSX.Element => {
    const disabled: boolean = this.getDisabledStatus(printOptionsPage, element);

    switch (element.elementType) {
      case AccessReportsWizardElementTypeEnum.Checkbox:
        return (
          <React.Fragment>
            <div className="offset-md-5 col-md-7">
              <QESCheckbox
                checked={(element.defaultValue !== undefined && element.defaultValue !== null) ? element.defaultValue as boolean : false}
                className="form-check-input"
                disabled={disabled}
                id={`access-reports-admin__print-settings--${element.elementId}-checkbox`}
                onChange={() => this.props.onPrintOptionsChange(formGroup.id, element.elementId as keyof AccessReportsPrintOptions, !element.defaultValue)}
                label={element.title}
                containerClassName="form-check"
                testId={`access-reports-print-settings__${element.elementId}`}
              />
            </div>
          </React.Fragment>
        );
      case AccessReportsWizardElementTypeEnum.ColorPicker:
        const presetColors: string[] = element.options.map(x => x.color as string);

        return (
          <React.Fragment>
            <label className="col-md-5 text-left col-form-label">{element.title}</label>

            <div className="col-md-7 d-flex align-items-center">
              <div className="color-picker-swatch" onClick={() => this.onColorPickerClick(element.elementId as PrintSettingsElementEnum)}>
                <div className="color-picker-color" style={{ backgroundColor: element.defaultValue as string || "#000" }} />
              </div>

              {(this.state.displayColorPicker === element.elementId) &&
                <div className="color-picker-popover">
                  <div
                    className="color-picker-cover"
                    onClick={() => this.onColorPickerClose()}
                  />

                  <SketchPicker
                    color={element.defaultValue as string || "#000"}
                    disableAlpha={true}
                    presetColors={presetColors}
                    onChange={this.onColorPickerChange}
                  />
                </div>
              }
            </div>
          </React.Fragment>
        );
      case AccessReportsWizardElementTypeEnum.Dropdown:
        return (
          <React.Fragment>
            <label className="col-md-5 text-left col-form-label">{element.title}</label>

            <div className="col-md-7">
              <select
                className="form-control"
                disabled={disabled}
                id={`access-reports-admin__print-settings--${element.elementId}-dropdown`}
                onChange={(e) => this.props.onPrintOptionsChange(formGroup.id, element.elementId as keyof AccessReportsPrintOptions, e.target.value)}
                value={(element.defaultValue !== undefined && element.defaultValue !== null) ? element.defaultValue.toString() : ""}
              >
                <option key="<none>" value="" />
                {element.options && element.options.map((option, optionIndex) => (
                  <option
                    key={optionIndex}
                    value={option.id}
                  >
                    {option.displayName ? option.displayName : option.name}
                  </option>
                ))}
              </select>
            </div>
          </React.Fragment>
        );
      case AccessReportsWizardElementTypeEnum.Input:
        return (
          <React.Fragment>
            <label className="col-md-5 text-left col-form-label">{element.title}</label>

            <div className="col-md-7">
              <input
                className="form-control"
                disabled={disabled}
                id={`access-reports-admin__print-settings--${element.elementId}-input`}
                onChange={(e) => this.props.onPrintOptionsChange(formGroup.id, element.elementId as keyof AccessReportsPrintOptions, e.target.value)}
                value={(element.defaultValue !== undefined && element.defaultValue !== null) ? element.defaultValue.toString() : ""}
              />
            </div>
          </React.Fragment>
        );
      case AccessReportsWizardElementTypeEnum.DatePicker:
        return (
          <div className="offset-md-5 col-md-7">
            <input
              className="form-control"
              disabled={disabled}
              id={`access-reports-admin__print-settings--${element.elementId}-datepicker`}
              onChange={(event) => this.props.onPrintOptionsChange(formGroup.id, element.elementId as keyof AccessReportsPrintOptions, event.target.value)}
              type="datetime-local"
              value={(element.defaultValue !== undefined && element.defaultValue !== null) ? element.defaultValue.toString() : ""}
            />
          </div>
        );
      default:
        console.error(`Unable to build a print options page form element for ${element.elementId} of type ${element.elementType}`);
    }
  }

  private disableReset = (inProgressPrintOptionsPage: AccessReportsWizardPage): boolean => {
    if (!this.props.disableUserActions) {
      const printOptionsElements: AccessReportsWizardElement[] = flatten(inProgressPrintOptionsPage.formGroups.map(x => x.elements));

      for (const element of printOptionsElements) {
        if (element.defaultValue !== element.originalDefaultValue) {
          return false;
        }
      }
    }

    return true;
  }

  private disableSave = (inProgressPrintOptionsPage: AccessReportsWizardPage): boolean => {
    const currentlySavedTemplate: AccessReportsWizardTemplate = this.props.accessReportsWizard.accessReportWizardVersions.find(
      x => x.accessReportWizardVersionId === this.props.selectedVersion.accessReportWizardVersionId
    )?.template;
    const currentlySavedPrintOptions: AccessReportsWizardPage = currentlySavedTemplate.pages.find((x) => x.title === AccessReportsWizardStepEnum.PrintOptions);

    if (isEqual(currentlySavedPrintOptions, inProgressPrintOptionsPage) || this.props.disableUserActions) {
      return true;
    }

    return false;
  }

  private displayResetAlert = (): void => {
    this.setState({
      displayResetAlert: true,
    });
  }

  private getDisabledStatus = (
    printOptionsPage: AccessReportsWizardPage,
    element: AccessReportsWizardElement
  ): boolean => {
    if (this.props.disableUserActions) {
      return true;
    } else {
      if (element.disabled) {
        // if the element is defined as disabled by the template
        return true;
      } else {
        if (element.previousRequiredElementId) {
          // if the element's accessibility depends on a previous required element
          for (const formGroup of printOptionsPage.formGroups) {
            for (const e of formGroup.elements) {
              if (e.elementId === element.previousRequiredElementId) {
                // if the previous required element must have a specific value
                if (element.previousRequiredValueId !== null && element.previousRequiredValueId !== undefined) {
                  if (e.defaultValue.toString() !== element.previousRequiredValueId.toString()) {
                    return true;
                  }
                } else {
                  // if the previous required element does not need a specific value,
                  // but a value of some sort does indeed need to be present
                  if (e.defaultValue === null || e.defaultValue === undefined || e.defaultValue === false) {
                    return true;
                  }
                }
              }
            }
          }
        }
      }
    }

    return false;
  }

  private hideResetAlert = (hideResetConfirmed: boolean): void => {
    if (hideResetConfirmed) {
      this.props.onResetPrintSettingsClick();
    }

    this.setState({
      displayResetAlert: false,
    });
  }

  private onColorPickerClick = (printSettingsElementId: PrintSettingsElementEnum): void => {
    if (printSettingsElementId === this.state.displayColorPicker) {
      this.setState({
        displayColorPicker: null,
      });
    } else {
      this.setState({
        displayColorPicker: printSettingsElementId,
      });
    }
  }

  private onColorPickerChange = (color: ColorResult): void => {
    const printOptionsPage: AccessReportsWizardPage = this.props.selectedVersion.template.pages.find((x) => x.title === AccessReportsWizardStepEnum.PrintOptions);
    let formGroupId: number = null;

    for (const formGroup of printOptionsPage.formGroups) {
      const element = formGroup.elements.find(x => x.elementId === this.state.displayColorPicker)

      if (!!element) {
        formGroupId = formGroup.id;
        this.props.onPrintOptionsChange(formGroupId, this.state.displayColorPicker, color.hex);

        break;
      }
    }
  }

  private onColorPickerClose = (): void => {
    this.setState({
      displayColorPicker: null,
    });
  }
}

export { AccessReportsAdminPrintSettingsTab };
